<template>
  <div>
    <div class="px-4 py-2 flex items-center justify-between mb-4">
      <h3
        id="menus.title"
        class="text-lg text-center font-medium text-gray-900 flex-grow"
      >
        {{ translate("Categories") }}
      </h3>
      <div class="flex gap-4">
        <button
          id="menus.opencreatemenumodal"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          @click="isNewMenuModalOpen = true"
          v-if="!isFoodics"
        >
          <i class="ni ni-fat-add mr-2"></i>
          <span>{{ translate("New Category") }}</span>
        </button>
        <div class="flex shadow-lg bg-white rounded-md cursor-pointer overflow-hidden">
          <div
            class="w-12 h-12 flex justify-center items-center transition"
            @click="viewType = 'grid'"
            :class="viewType === 'grid' && 'bg-gray text-white'">
              <i class="fa fa-th-large"></i>
          </div>
          <div
            class="w-12 h-12 flex justify-center items-center transition"
            @click="viewType = 'list'"
            :class="viewType === 'list' && 'bg-gray text-white'">
              <i class="fa fa-bars"></i>
          </div>
        </div>
      </div>
    </div>

    <div class="container mx-auto px-4" v-if="viewType === 'list'">
      <!-- Menu List Component -->
      <CategoriesList
        :list="list"
        :loading="loading"
        @getCategories="getCategories"
        @enableDisableCategory="enableDisableCategory"
        @onDeleteClicked="confirmAction('delete', $event)"
      />
    </div>

    <div class="container mx-auto px-4" v-else-if="viewType === 'grid'">
      <CategoriesGrid
        :list="list"
        :loading="loading"
        @enableDisableCategory="enableDisableCategory"
        @onDeleteClicked="confirmAction('delete', $event)"
      />
    </div>

    <!-- Modals -->

    <CategoriesModal
      v-if="isNewMenuModalOpen"
      :isOpen="isNewMenuModalOpen"
      @close="isNewMenuModalOpen = false"
      @getCategories="getCategories"
    />

    <!-- Generic Confirmation Modal -->
    <BaseModal
      v-if="showConfirmModal"
      :title="confirmationConfig.title"
      @close="cancelAction"
      headerClass="py-3"
    >
      <template v-slot:body>
        <div class="flex items-center py-3">
          <div
            class="mr-2 flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full"
            :class="confirmationConfig.iconBgClass"
          >
            <i :class="confirmationConfig.iconClass"></i>
          </div>
          <div class="text-center sm:text-left">
            <p class="text-lg mb-0 font-medium">
              {{ confirmationConfig.message }}
            </p>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <div class="w-full flex justify-end py-3">
          <button
            :id="confirmationConfig.cancelButtonId"
            @click="cancelAction"
            class="rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
          >
            {{ translate("Cancel") }}
          </button>
          <button
            :id="confirmationConfig.confirmButtonId"
            @click="executeAction"
            class="rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
            :class="confirmationConfig.confirmButtonClass"
          >
            {{ confirmationConfig.confirmButtonText }}
          </button>
        </div>
      </template>
    </BaseModal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { eventHandler, translations } from '@/mixins';
import CategoriesList from './components/CategoriesList';
import CategoriesGrid from './components/CategoriesGrid.vue';
import { BaseModal } from '@/components';
import { deleteCategory, enableDisableSpecificCategory, getAllCategoriesByMenu } from '@/services/V2/menus/categories.service';
import CategoriesModal from './components/CategoriesModal.vue';

export default {
  name: 'CategoriesLanding',

  components: {
    CategoriesList,
    CategoriesGrid,
    BaseModal,
    CategoriesModal,
  },

  mixins: [eventHandler, translations],

  computed: {
    ...mapGetters({
      getLocale: 'app/getLocale',
      getUserClient: 'account/getUserClient',
    }),

    isFoodics () {
      return this.getUserClient?.attributes?.label === 'Foodics Online';
    },

    confirmationConfig () {
      const configs = {
        delete: {
          title: this.translate('Delete Category'),
          message: this.translate('Are you sure you want to delete this catgeory?'),
          iconClass: 'fas fa-trash-alt text-red-600',
          iconBgClass: 'bg-red-100',
          confirmButtonText: this.translate('Delete'),
          confirmButtonClass: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
          confirmButtonId: 'menus.deletemenu',
          cancelButtonId: 'menus.closedeletemenumodal',
        },
      };
      return configs[this.currentAction] || configs.delete;
    },
  },

  data () {
    return {
      isNewMenuModalOpen: false,
      list: [],
      loading: false,
      page: 1,
      showConfirmModal: false,
      currentAction: null,
      menuId: null,
      categoryId: null,
      viewType: 'list', // types: ('list', 'grid')
    };
  },

  mounted () {
    this.menuId = this.$route?.params['menuId'];
    this.getCategories();
  },

  methods: {
    async getCategories () {
      this.loading = true;
      try {
        const response = await getAllCategoriesByMenu(this.menuId);
        this.list = response?.data?.map((category) => ({
          id: category?.id,
          name: category?.attributes?.name,
          imageURI: category?.attributes?.['image-uri'],
          enabled: category?.attributes?.enabled,
        }));
      } catch (error) {
        this.errorToast('ERROR!', 'Error fetching categories!');
      } finally {
        this.loading = false;
      }
    },

    confirmAction (action, categoryId) {
      this.currentAction = action;
      this.categoryId = categoryId;
      this.showConfirmModal = true;
    },

    cancelAction () {
      this.showConfirmModal = false;
      this.currentAction = null;
      this.categoryId = null;
    },

    async executeAction () {
      if (!this.categoryId) return;

      try {
        await deleteCategory(this.menuId, this.categoryId);
        this.successToast(
          'CATEGORY DELETED!',
          'Category has been deleted successfully',
        );
        this.getCategories();
      } catch (error) {
        this.errorToast('SYSTEM ERROR!', 'Something went wrong, please try again!');
      } finally {
        this.showConfirmModal = false;
        this.currentAction = null;
        this.categoryId = null;
      }
    },

    successToast (title, message) {
      this.$notify({
        title: title,
        verticalAlign: 'bottom',
        horizontalAlign: 'left',
        message: message,
        type: 'success',
        icon: 'fas fa-check',
      });
    },

    errorToast (title, message) {
      this.$notify({
        title: title,
        verticalAlign: 'bottom',
        horizontalAlign: 'left',
        message: message,
        type: 'danger',
        icon: 'fas fa-bomb',
      });
    },

    async enableDisableCategory (category) {
      try {
        await enableDisableSpecificCategory(this.menuId, category?.id, !category?.enabled);
        this.successToast('Updated!', 'Category Updated Successfully');
        this.getCategories();
      } catch (e) {
        this.errorToast('ERROR!', 'Something went wrong!');
      }
    },
  },
};
</script>
